/**
 * The Allowed callers list on the privacy settings page: the members the
 * viewer has an accepted call request with, in either direction.
 */

import { callApi } from './call-api.js';
import { avatarUrl } from './call-state.js';

const PAGE_SIZE = 50;

function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

export function allowedCallers() {
    return {
        open: false,
        loading: true,
        failed: false,
        people: [],
        hasMore: false,
        nextOffset: 0,
        removing: '',

        init() {
            this.load(0);
        },

        async load(offset) {
            this.loading = true;
            this.failed = false;
            try {
                const page = await callApi.listAuthorized(offset, PAGE_SIZE);
                const rows = (page.data || []).map((item) => ({
                    id: String(item.user?.id || ''),
                    name: item.user?.name || item.user?.username || '',
                    username: item.user?.username || '',
                    avatar: avatarUrl(item.user),
                })).filter((row) => row.id);
                this.people = offset === 0 ? rows : this.people.concat(rows);
                this.hasMore = page.meta?.has_more === true;
                this.nextOffset = Number(page.meta?.next_offset) || 0;
            } catch {
                this.failed = true;
            } finally {
                this.loading = false;
                this.$nextTick(() => window.LucideIcons?.initialize(this.$el));
            }
        },

        toggle() {
            this.open = !this.open;
            if (this.open) {
                this.$nextTick(() => window.LucideIcons?.initialize(this.$el));
            }
        },

        countText() {
            const count = this.people.length;
            if (this.loading && count === 0) {
                return '';
            }
            if (this.hasMore) {
                return t('settings.allowed_callers_count_more', { count });
            }
            return count === 1
                ? t('settings.allowed_callers_count_one', { count })
                : t('settings.allowed_callers_count_other', { count });
        },

        turnOffLabel(person) {
            return t('settings.allowed_callers_turn_off_for', { name: person.name });
        },

        async turnOff(person) {
            if (this.removing) {
                return;
            }
            this.removing = person.id;
            try {
                await callApi.revokeAuthorization(person.id);
                this.people = this.people.filter((p) => p.id !== person.id);
                // The next page starts one row earlier after a removal.
                this.nextOffset = Math.max(0, this.nextOffset - 1);
                document.dispatchEvent(new CustomEvent('calls:authorization-changed', {
                    detail: { userId: person.id },
                }));
            } catch {
                window.showError?.(t('common.calls_error_unavailable'));
            } finally {
                this.removing = '';
            }
        },
    };
}
